/**
 * Shared base-URL resolver for RTK Query slices.
 *
 * Implements the fix pattern documented in
 * docs/known-fix-patterns/rtk-query-base-url-localhost-fallback.md —
 * uses ?? (nullish coalescing) so an explicit empty string never silently
 * triggers the localhost fallback, and uses a window-detection guard so
 * Node/test environments receive an absolute URL rather than a relative path.
 *
 * The result is the API ROOT: every consumer appends its own sub-path
 * (`/families/...`, `${base}/announcements`, ...) with no `/api` segment of
 * its own. An explicit absolute `VITE_API_URL` that omits `/api` (e.g. a bare
 * `https://kindash.com`) would therefore route every request at the SPA
 * catch-all, which answers 200 `text/html` — see
 * docs/known-fix-patterns/rtk-query-base-url-missing-api-suffix.md (#4395,
 * #16878). Absolute URLs are normalized here so a misconfigured deploy
 * variable self-corrects instead of breaking every consumer at once.
 *
 * Parameters are NOT defaulted here — callers pass explicit values for
 * testability (avoiding "undefined triggers default" surprises when tests
 * stub VITE_API_URL and then call the function with undefined to exercise
 * the fallback paths). At call sites use:
 *
 *   getRtkBaseUrl(import.meta.env.VITE_API_URL, typeof window !== 'undefined')
 */
export function getRtkBaseUrl(
  viteApiUrl: string | undefined,
  hasWindow: boolean,
): string {
  const resolved = viteApiUrl ?? (hasWindow ? '/api' : 'http://localhost:3000/api');
  // Only absolute http(s) URLs are normalized: a relative value ('/api') and the
  // explicit empty string are deliberate and must pass through untouched.
  if (!/^https?:\/\//i.test(resolved)) return resolved;
  const trimmed = resolved.replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
}
